// @ts-strict-ignore
import { UndoReplaceArticleOperation } from './undo-replace-article-operation';
import { IMoveArticlesOperation } from './i-move-articles-operation';
import { Dictionary } from 'typescript-collections';
import { Euler, Matrix4, Vector3 } from 'three';
import { Connection } from '@3d-scene/model3d/connection';
import { UndoAttachArticlesOperation } from './undo-attach-articles-operation';
import { UndoDetachArticlesOperation } from './undo-detach-articles-operation';
import { ProjectModel } from '@project/model/project.model';
import { AlternativeUsageConnectionData } from '@project/components/article/model/alternative-usage-connection-data';
import { BaseArticleOperation } from './base-article-operation';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { ConnectionAreaUsageVO } from '../../vo/meta-element.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { CloneUtils } from '../../util/clone-utils';
import { ArrayUtils } from '../../util/array-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { LabelingArticleRotationHelper } from '@project/components/label/helper/labeling-article-rotation.helper';
import { BaseArticlesSequenceOperation } from './base-articles-sequence-operation';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { RotateArticlePresenter } from '@project/components/article/presenter/rotate-article.presenter';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { NumberUtils } from '../../util/number-utils';
import { InsertArticleUtils } from '@project/components/article/util/insert-article-utils';

export class UndoAlternativeReplaceArticleOperation extends UndoReplaceArticleOperation implements IMoveArticlesOperation {
  private _moveArticlesMap: Dictionary<string, Vector3>;
  private _rotateArticlesMap: Dictionary<string, Matrix4>;
  private _givingConnections: Connection[] = [];
  private _connectedArticleUsages: ArticleUsageVO[] = [];
  private _alternativeUsageConnectionDatas: Dictionary<string, AlternativeUsageConnectionData[]> = new Dictionary<
    string,
    AlternativeUsageConnectionData[]
  >();

  constructor(
    attachOperation: UndoAttachArticlesOperation,
    detachOperation: UndoDetachArticlesOperation,
    alternativeUsageConnectionDatas: Dictionary<string, AlternativeUsageConnectionData[]>,
    moveArticlesMap: Dictionary<string, Vector3>,
    rotateAlrticlesMap: Dictionary<string, Matrix4>,
    private insertArticleUtils: InsertArticleUtils,
    private containerService: ContainerService,
    private labelingArticleRotationHelper: LabelingArticleRotationHelper,
    private rotateArticlePresenter: RotateArticlePresenter,
    private projectModel: ProjectModel
  ) {
    super(attachOperation, detachOperation);
    this._alternativeUsageConnectionDatas = alternativeUsageConnectionDatas;
    this._moveArticlesMap = moveArticlesMap;
    this._rotateArticlesMap = rotateAlrticlesMap;
  }

  public getMoveArticlesMap(): Dictionary<string, Vector3> {
    return this._moveArticlesMap;
  }

  public isArticlesOrderChanged(): boolean {
    return false;
  }

  public isDistanceBetweenArticlesChanged(): boolean {
    return true;
  }

  public getQualifiedClassName(): string {
    return 'UndoAlternativeReplaceArticleOperation';
  }

  public async performUndo(): Promise<void> {

    this.insertArticleUtils.moveArticlesByMap(this._moveArticlesMap, true);
    this.applyRotationTranform(true);

    let usageIds: string[] = this.getUsageIdsFromOperation(this._attachArticlesOperation);
    this.preProcess(usageIds, this.projectModel);

    await super.performUndo();

    usageIds = this.getUsageIdsFromOperation(this._detachArticlesOperation);
    this.postProcess(usageIds, this.projectModel, true);

    this.cleanup();
  }

  public async performRedo(): Promise<void> {
    let usageIds: string[] = this.getUsageIdsFromOperation(this._detachArticlesOperation);
    this.preProcess(usageIds, this.projectModel, true);

    await super.performRedo();

    this.applyRotationTranform(false);
    this.insertArticleUtils.moveArticlesByMap(this._moveArticlesMap, false);

    usageIds = this.getUsageIdsFromOperation(this._attachArticlesOperation);
    this.postProcess(usageIds, this.projectModel);

    this.cleanup();
  }

  private applyRotationTranform(inverseRotation: boolean = false): void {
    const articleGUIDs: string[] = [];
    for (const guid of this._rotateArticlesMap.keys()) {
      articleGUIDs.push(guid);
    }
    const containersToRotate: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(articleGUIDs);
    this.rotateArticlesByTransform(containersToRotate, inverseRotation);
    this.rotateArticlesByAppliedAngle(containersToRotate);
  }

  private rotateArticlesByTransform(articleContainers: ArticleContainer[], inverseRotation: boolean = false): void {
    for (const container of articleContainers) {
      if (!this._rotateArticlesMap.containsKey(container.articleGUID)) {
        continue;
      }

      let rotMatrix: Matrix4 = this._rotateArticlesMap.getValue(container.articleGUID);
      if (!rotMatrix) {
        continue;
      }

      rotMatrix = rotMatrix.clone();
      if (inverseRotation) {
        rotMatrix.copy(rotMatrix).invert();
      }
      container.rotateByEuler(new Euler().setFromRotationMatrix(rotMatrix));
      if (ArticleUtils.isLabelingArticle(container.article)) {
        this.labelingArticleRotationHelper.rotateLabelingArticle(this.rotateArticlePresenter, container);
      }
    }
  }

  private rotateArticlesByAppliedAngle(containers: ArticleContainer[]): void {
    const articlesToRotate: ArticleContainer[] = this.getAccessoryArticlesReplacedArticles(containers);
    for (const articleContainer of articlesToRotate) {
      const appliedRotationAngle: number = ArticleUsageUtils.getArticleUsagePlausiPropertyIntValue(
        articleContainer.articleUsage,
        ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE
      );
      if (NumberUtils.isNumber(appliedRotationAngle)) {
        this.rotateArticlePresenter.rotateArticleContainer(articleContainer, appliedRotationAngle, false, true, true, true, false);
      }
    }
  }

  private getAccessoryArticlesReplacedArticles(containers: ArticleContainer[]): ArticleContainer[] {
    const accessoryArticleIds: string[] = [];
    for (const connectionDatas of this._alternativeUsageConnectionDatas.values()) {
      for (const connectionData of connectionDatas) {
        accessoryArticleIds.push(connectionData.articleUsageId);
      }
    }
    return ArticleContainerUtils.findArticleContainerByGUIDs(accessoryArticleIds, containers);
  }

  private getUsageIdsFromOperation(operation: BaseArticlesSequenceOperation): string[] {
    const usageIds: string[] = [];
    ArrayUtils.mergeArrays(usageIds, this.extractArticleUsageIds(operation.dynamicArticleOperations));
    ArrayUtils.mergeArrays(usageIds, this.extractArticleUsageIds(operation.articleOperations));
    return usageIds;
  }

  private preProcess(usageIds: string[], projectModel: ProjectModel, isRedoProcessed: boolean = false): void {
    for (const usageId of usageIds) {
      // TODO: (from flex) remove unnecessary check and flags. Will be better use getAlternativeUsageConnectionDatasByParentUsageId
      if (isRedoProcessed && !this._alternativeUsageConnectionDatas.getValue(usageId)) {
        continue;
      }

      const articleUsage: ArticleUsageVO = projectModel.getArticleUsage(usageId);
      if (!articleUsage) {
        continue;
      }

      const alternativeUsageConnectionDatas: AlternativeUsageConnectionData[] = this.getAlternativeUsageConnectionDatasByParentUsageId(usageId);
      const foundGivingConnections: Connection[] = this.getGivingConnectionsByCommonArticleUsage(articleUsage, alternativeUsageConnectionDatas);

      let givingUsageId: string;
      for (const connection of foundGivingConnections) {
        givingUsageId = connection.articleContainer.articleGUID;
        this._connectedArticleUsages.push(projectModel.getArticleUsage(givingUsageId));
        ArticleUsageUtils.removeAssociationsWithUsage(articleUsage, givingUsageId);
      }
      this._givingConnections = this._givingConnections.concat(foundGivingConnections);
    }
  }

  private postProcess(usageIds: string[], projectModel: ProjectModel, isUndoOperation: boolean = false): void {
    for (const usageId of usageIds) {
      // TODO: (from flex) remove unnecessary check and flags.
      if (isUndoOperation && !this._alternativeUsageConnectionDatas.getValue(usageId)) {
        continue;
      }

      const alternativeUsageConnectionDatas: AlternativeUsageConnectionData[] = this.getAlternativeUsageConnectionDatasByParentUsageId(usageId);
      if (ArrayUtils.isEmpty(alternativeUsageConnectionDatas)) {
        continue;
      }

      const firstConnectionData: AlternativeUsageConnectionData = alternativeUsageConnectionDatas[0];
      if (!firstConnectionData) {
        continue;
      }

      const alternativeUsageId: string = firstConnectionData.getAlternativeParentUsageId(usageId);
      const articleUsage: ArticleUsageVO = projectModel.getArticleUsage(usageId);
      if (articleUsage) {
        ArticleUsageUtils.replaceArticleUsageId([articleUsage], alternativeUsageId, usageId);
      }
      ArticleUsageUtils.replaceArticleUsageId(this._connectedArticleUsages, alternativeUsageId, usageId);

      this.reconnectAccessoryArticles(usageId, alternativeUsageConnectionDatas);
    }
  }

  private reconnectAccessoryArticles(
    usageId: string,
    alternativeUsageConnectionDatas: AlternativeUsageConnectionData[]
  ): void {
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
    let acceptingConnection: Connection;
    let givingConnection: Connection;
    for (const alternativeUsageConnectionData of alternativeUsageConnectionDatas) {
      acceptingConnection = articleContainer.findFirstConnectionByName(
        alternativeUsageConnectionData.getConnectionNameByParentId(articleContainer.articleGUID)
      );
      if (!acceptingConnection) {
        continue;
      }

      givingConnection = this.getGivingConnectionByUsageId(
        alternativeUsageConnectionData.articleUsageId,
        alternativeUsageConnectionData.givingConnectionName
      );
      if (!givingConnection) {
        continue;
      }

      // giving area-usage and corresponding accepting area-usage of replaced article might refer to the same instance of ConnectionVO
      // so clone of connection on re-connected article should be used to update information about accepting-connection
      // so that ConnectionVO of replaced article is not "corrupted" by replacement
      const givingArea: ConnectionAreaUsageVO = givingConnection.metaElementUsage as ConnectionAreaUsageVO;
      givingArea.connection = CloneUtils.cloneConnectionVO(givingArea.connection);
      givingArea.connection.acceptingAreaId.metaElementName = acceptingConnection.connectionName;
    }
  }

  private getAlternativeUsageConnectionDatasByParentUsageId(parentUsageId: string): AlternativeUsageConnectionData[] {
    let result: AlternativeUsageConnectionData[] = this._alternativeUsageConnectionDatas.getValue(parentUsageId);
    if (!result) {
      let alternativeUsageConnectionDatas: AlternativeUsageConnectionData[];
      for (const oldParentUsageId of this._alternativeUsageConnectionDatas.keys()) {
        alternativeUsageConnectionDatas = this._alternativeUsageConnectionDatas.getValue(oldParentUsageId);
        const firstAlternativeConnectionData: AlternativeUsageConnectionData = alternativeUsageConnectionDatas[0];
        if (firstAlternativeConnectionData && firstAlternativeConnectionData.newParentUsageId === parentUsageId) {
          result = alternativeUsageConnectionDatas;
          break;
        }
      }
    }

    return result;
  }

  private getGivingConnectionByUsageId(givingId: string, givingConnectionName: string): Connection {
    for (const connection of this._givingConnections) {
      if (!connection.articleContainer) {
        continue;
      }
      if (connection.articleContainer.articleGUID === givingId && connection.connectionName === givingConnectionName) {
        return connection;
      }
    }
    return null;
  }

  private getGivingConnectionsByCommonArticleUsage(
    articleUsage: ArticleUsageVO,
    alternativeUsageConnectionDatas: AlternativeUsageConnectionData[]
  ): Connection[] {
    const givingConnections: Connection[] = [];

    for (const connection of ArticleUsageUtils.connections(articleUsage)) {
      if (connection.acceptingAreaId.articleUsageId === articleUsage.id) {
        for (const givingIdentity of connection.givingAreaIds) {
          if (!this.isReconnectedUsage(givingIdentity.articleUsageId, alternativeUsageConnectionDatas)) {
            continue;
          }

          const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(givingIdentity.articleUsageId);
          const givingConnection: Connection = container.findFirstConnectionByName(givingIdentity.metaElementName);
          if (connection && givingConnections.indexOf(givingConnection) === -1) {
            givingConnections.push(givingConnection);
          }
        }
      }
    }

    return givingConnections;
  }

  private isReconnectedUsage(usageId: string, alternativeUsageConnectionDatas: AlternativeUsageConnectionData[]): boolean {
    if (!usageId || !alternativeUsageConnectionDatas) {
      return true;
    }

    for (const alternativeUsageConnectionData of alternativeUsageConnectionDatas) {
      if (alternativeUsageConnectionData.articleUsageId === usageId) {
        return true;
      }
    }

    return false;
  }

  private extractArticleUsageIds(articleOperations: BaseArticleOperation[]): string[] {
    const usageIds: string[] = [];
    for (const operation of articleOperations) {
      usageIds.push(operation.articleUsageId);
    }
    return usageIds;
  }

  private cleanup(): void {
    this._givingConnections = [];
    this._connectedArticleUsages = [];
  }
}
